// @flow

import React, { useState, useRef, type Node } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { __ } from '../../../i18n/l10n';
import { transparentize } from 'polished';
import { fontFamily, sizes, colorPalette, timings } from '../../../styles/GlobalStyle';
import type { Option } from '../types';

type DropZoneProps = {|
  className?: string,
  types?: Option[],
  children: Node,
  id?: string,
  onFileSelection?: (event: string) => void
|};

const DropZoneRaw = ({ className, types = [], children, onFileSelection, id = '' }: DropZoneProps) => {
  const [over, setOver] = useState(false);
  const nativeFileInput = useRef<HTMLInputElement | null>(null);
  const [dropDisabled, setDropDisabled] = useState(false);

  const onDragEnter = (event) => {
    event.preventDefault();
    setOver(true);
  };

  const onDragLeave = (event) => {
    event.preventDefault();
    setOver(false);
  };

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const onDrop = (event) => {
    event.preventDefault();
    setOver(false);

    if (!dropDisabled && onFileSelection) {
      onFileSelection(event.dataTransfer.files);
    }
  };

  const openNativeFileInput = () => {
    if (nativeFileInput.current) {
      nativeFileInput.current.click();
      setDropDisabled(true);
      if (document.body) {
        document.body.onfocus = () => {
          setDropDisabled(false);
        };
      }
    }
  };

  const onFileInputChange = (event) => {
    if (onFileSelection) {
      onFileSelection(event.target.files);
    }
  };

  const renderTypes = () => {
    if (types && types.length > 0) {
      return (
        <p className="formats">
          {__('Acceptable formats: %1$s.', types.map((type) => type.label.toUpperCase()).join(', '))}
        </p>
      );
    }

    return '';
  };

  return (
    <div className={className}>
      <label htmlFor={id} className="btn-1">
        <input
          className="nativeInput"
          ref={nativeFileInput}
          type="file"
          onChange={onFileInputChange}
          accept={types && types.map((type) => type.value).join(',')}
          id={`${id}Input`}
        />
        <div
          className={`dropZone${over ? ' over' : ''}${dropDisabled ? ' disabled' : ''}`}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeave}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onClick={openNativeFileInput}
          onKeyDown={() => {}}
          role="button"
          tabIndex={0}
          id={`${id}Area`}
        />
        <div className="content">
          <div className="children-container">{dropDisabled ? __('Select your file') : children}</div>
          {renderTypes()}
        </div>
      </label>
    </div>
  );
};

export const DropZone: StyledComponent<DropZoneProps, {}, typeof DropZoneRaw> = styled(DropZoneRaw)`
  position: relative;
  height: 100%;

  .dropZone {
    height: 100%;
    border: 2px dashed ${colorPalette.primary.active};
    border-radius: ${sizes.borderRadius};
    transition: background ${timings.hover};
    cursor: pointer;

    &.over {
      background-color: ${transparentize(0.5, colorPalette.primary.hover)};
    }

    &.disabled {
      &.over {
        background-color: transparent;
      }
    }
  }

  .content {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 100%;
    max-height: 100%;
    user-select: none;
    z-index: -1;
    color: ${colorPalette.primary.active};
    text-align: center;
    transform: translate(-50%, -50%);
  }

  .children-container {
    font-weight: ${fontFamily.primary.black.fontWeight};
  }

  .nativeInput {
    display: none;
  }
`;
